import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import shipmentdelivered from '../assets/globe/g1.png';
import countryserved from '../assets/globe/g2.png';
import ontime from '../assets/globe/g3.png';
import customersupport from '../assets/globe/g4.png';
import enterpriseclient from '../assets/globe/g5.png';
import customclearence from '../assets/globe/g6.png';
import globevideo from '../assets/globe/globe-vid.mp4';
import lines from "../assets/globe/lines.png";

import RotatingMap from './RotatingMap'; // adjust path if needed
import * as topojson from 'topojson-client';




// Custom hook for counting animation - only runs once per page load
const useCountUp = (end, isActive, duration = 2000, delay = 0) => {
  const [count, setCount] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    // Only start animation if active and hasn't completed yet
    if (!isActive || hasCompleted) return;
    
    const timer = setTimeout(() => {
      let startTime = null;
      const startValue = 0;
      
      const animate = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentCount = Math.floor(startValue + (end - startValue) * easeOutCubic);
        
        setCount(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(end);
          setHasCompleted(true); // Mark as completed
        }
      };
      
      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timer);
  }, [end, isActive, duration, delay, hasCompleted]);

  // Return the final value if completed, otherwise return current count
  return hasCompleted ? end : count;
};

// Optimized StatCard component
const StatCard = React.memo(({ stat, index, inView, isMobile }) => {
  // Parse numeric values for counting animation
  const getNumericValue = (value) => {
    if (typeof value === 'string') {
      // Remove all non-digit characters except decimal points and commas
      const cleanValue = value.replace(/[^\d.,]/g, '');
      // Remove commas and parse as float
      const numericValue = parseFloat(cleanValue.replace(/,/g, ''));
      return isNaN(numericValue) ? 0 : numericValue;
    }
    return value;
  };

  const numericValue = getNumericValue(stat.value);
  const countedValue = useCountUp(numericValue, inView, 2000, index * 200);
  
  // Format the displayed value
  const getDisplayValue = (originalValue, countedValue) => {
    if (originalValue.includes('+')) {
      return `${countedValue.toLocaleString()}+`;
    } else if (originalValue.includes('%')) {
      return `${countedValue}%`;
    } else if (originalValue.includes('/')) {
      return originalValue; // Keep 24/7 as is
    }
    return countedValue.toLocaleString();
  };

  const displayValue = stat.value === '24/7' ? '24/7' : getDisplayValue(stat.value, countedValue);

  // Animation classes for mobile
  const getAnimationClass = () => {
    if (!isMobile) return '';
    if (index === 4 || index === 5) return 'animate-slide-in-bottom';
    return index % 2 === 0 ? 'animate-slide-in-left' : 'animate-slide-in-right';
  };

  const baseClasses = isMobile 
    ? "relative z-20 flex flex-col items-center justify-center text-center text-white p-4"
    : "absolute z-20 grid grid-cols-[auto_auto] grid-rows-[auto_auto] gap-y-2 text-white items-center justify-center text-center";

  return (
    <div 
      className={`${baseClasses} ${stat.desktopPosition || ''} ${inView ? (isMobile ? getAnimationClass() : 'animate-fade-in-scale') : 'opacity-0'}`}
      style={{ 
        animationDelay: `${index * (isMobile ? 0.15 : 0.2) + (isMobile ? 0.5 : 0)}s`,
        opacity: inView ? 1 : 0
      }}
    >
      <img 
        src={stat.icon} 
        alt={stat.label} 
        className={`transition-transform duration-300 hover:scale-110 ${
          isMobile ? 'h-16 mb-2' : 'h-[40px] lg:h-[60px]'
        }`}
        loading="lazy"
      />
      <div className={`font-bold ${
        isMobile ? 'text-3xl sm:text-3xl' : 'text-2xl lg:text-4xl'
      }`}>
        {displayValue}
      </div>
      <div className={`leading-snug ${
        isMobile ? 'text-lg sm:text-sm mt-1' : 'col-span-2 text-sm lg:text-xl'
      }`}>
        {stat.label}
      </div>
    </div>
  );
});

StatCard.displayName = 'StatCard';

// Optimized TypewriterText component (kept for potential future use)
const TypewriterText = React.memo(({ text, isActive }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      setCurrentIndex(0);
      return;
    }

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 90);

      return () => clearTimeout(timeout);
    }
  }, [text, currentIndex, isActive]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
});

TypewriterText.displayName = 'TypewriterText';

const Globe = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [inView, setInView] = useState(false);
  const [headingVisible, setHeadingVisible] = useState(false);

  const [geoJson, setGeoJson] = useState(null);

useEffect(() => {
  const loadGeoJson = async () => {
    try {
      const res = await fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson');
      const data = await res.json();
      setGeoJson(data);
    } catch (err) {
      console.error('Failed to load GeoJSON:', err);
    }
  };
  loadGeoJson();
}, []);



  const sectionRef = useRef(null);
  const observerRef = useRef(null);

  // Memoized stats data
  const stats = useMemo(() => [
    { 
      id: 1, 
      icon: countryserved, 
      value: "130+", 
      label: "Countries Served",
      desktopPosition: "-left-[260px] top-[0px]",
    },
    { 
      id: 2, 
      icon: shipmentdelivered, 
      value: "50,000+", 
      label: "Shipments Delivered",
      desktopPosition: "-top-[130px] left-1/2 -translate-x-1/2",
    },
    { 
      id: 3, 
      icon: enterpriseclient, 
      value: "300+", 
      label: "Enterprise Clients",
      desktopPosition: "-right-[190px] top-[90px]",
    },
    { 
      id: 4, 
      icon: customclearence, 
      value: "100%", 
      label: "Success Rate",
      desktopPosition: "top-[90%] -left-[130px] -translate-y-1/2",
    },
    { 
      id: 5, 
      icon: ontime, 
      value: "98.7%", 
      label: "On-Time Delivery Rate",
      desktopPosition: "top-[90%] -right-[220px] -translate-y-1/2",
    },
    { 
      id: 6, 
      icon: customersupport, 
      value: "24/7", 
      label: "Customer Support",
      desktopPosition: "-bottom-[150px] left-1/2 -translate-x-1/2",
    }
  ], []);

  // Optimized resize handler
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  // Optimized intersection observer callback
  const handleIntersection = useCallback(([entry]) => {
    if (entry.isIntersecting) {
      setInView(true);
      setTimeout(() => setHeadingVisible(true), 300);
    } else {
      setInView(false);
      setHeadingVisible(false);
    }
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Create intersection observer
    observerRef.current = new IntersectionObserver(handleIntersection, { threshold: 0.1 });
    
    if (sectionRef.current) {
      observerRef.current.observe(sectionRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (observerRef.current && sectionRef.current) {
        observerRef.current.unobserve(sectionRef.current);
      }
    };
  }, [checkMobile, handleIntersection]);

  // Memoized background style
  const backgroundStyle = useMemo(() => {
    return !isMobile ? {
      backgroundImage: `url(${lines})`,
      backgroundSize: '600px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center'
    } : {};
  }, [isMobile]);

  return (
    <>
      {/* Optimized CSS - moved to a separate style tag for better performance */}
      <style>
        {`
          @keyframes fadeInScale {
            0% { opacity: 0; transform: scale(0.5); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          @keyframes slideInLeft {
            0% { opacity: 0; transform: translateX(-100px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          
          @keyframes slideInRight {
            0% { opacity: 0; transform: translateX(100px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          
          @keyframes slideInBottom {
            0% { opacity: 0; transform: translateY(50px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          
          .animate-fade-in-scale {
            animation: fadeInScale 1.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          }
          
          .animate-slide-in-left {
            animation: slideInLeft 0.8s ease-out forwards;
          }
          
          .animate-slide-in-right {
            animation: slideInRight 0.8s ease-out forwards;
          }
          
          .animate-slide-in-bottom {
            animation: slideInBottom 0.8s ease-out forwards;
          }
        `}
      </style>

      <section
        ref={sectionRef}
        className="relative w-full min-h-[100vh] md:h-[100vh] overflow-hidden flex justify-center items-center"
        style={backgroundStyle}
      >
        {/* Desktop Globe with surrounding stats */}
        {!isMobile && (
          <div className="relative w-[400px] h-[400px] lg:w-[400px] lg:h-[400px]">
            <RotatingMap geoJson={geoJson} size={400} />


            {stats.map((stat, index) => (
              <StatCard 
                key={stat.id}
                stat={stat}
                index={index}
                inView={inView}
                isMobile={false}
              />
            ))}
          </div>
        )}

      {/* Mobile - Stacked stats without globe */}
      <div className="md:hidden w-full h-full flex flex-col justify-center items-center py-0">
        <h2 
              className="text-3xl font-bold text-white text-center mb-8 px-4 min-h-[3rem]"
            >
              {inView && (
                <TypewriterText 
                  text="Our Global Reach" 
                  isActive={inView} 
                />
              )}
            </h2>
        
        <div className="relative w-full h-full grid grid-cols-2 gap-4 px-4">
          {stats.map((stat, index) => {
            // Determine animation class based on position
            let animationClass = '';
            if (index === 4 || index === 5) {
              animationClass = 'animate-slide-in-bottom';
            } else {
              animationClass = index % 2 === 0 
                ? 'animate-slide-in-left' 
                : 'animate-slide-in-right';
            }
            
            return (
              <div 
                key={stat.id}
                className={`relative z-20 flex flex-col items-center justify-center text-center text-white p-4 ${inView ? animationClass : 'opacity-0'}`}
                style={{ 
                  animationDelay: `${index * 0.15 + 0.5}s`,
                  opacity: inView ? 1 : 0
                }}
              >
                <img 
                  src={stat.icon} 
                  alt={stat.label} 
                  className="h-16 mb-2 transition-transform duration-300 hover:scale-110" 
                />
                <div className="text-3xl sm:text-3xl font-bold">{stat.value}</div>
                <div className="text-lg sm:text-sm mt-1">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </div>
      </section>
    </>
  );
};

export default Globe;